import {
  CountryPhoneCodeListDocument,
  UserDeleteDocument,
  UserContactDetailsFetchDocument,
  RoleListDocument,
  UserUpdateDocument,
  UserGetByIdDocument,
  UserCreateDocument,
  UserGetInvestorDetailsForCreateDocument,
  UserUpdateOwnAccountDocument,
  UsersFilterDocument,
  UsersListDocument,
  InvestorProfilesForUserCreationDocument,
  UserOtpResetDocument,
  UserListCategoriesDocument,
  UserListUsersCouldBeManagedDocument,
  DepartmentListDocument,
  DepartmentListDepartmentsCouldBeManagedDocument,
  UserListByDepartmentDocument,
  UserListByRoleDocument,
  UserGetByManagedDepartmentDocument,
  UserListEmployeesDocument,
  UserListCrstEmployeesDocument,
} from "api/generated";
import { successMessages } from "api/messages/success";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { InvestorProfilesNameReturnType } from "api";
import {
  NotificationDuration,
  NotificationMode,
  showSnackBar,
  store,
} from "store";
import {
  DepartmentEnum,
  Nullable,
  Optional,
  PartialSelectedAndNullable,
  ResourceEnum,
  ScopeEnum,
  mapInvestorName,
} from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IGetUserReturnType,
  ICountryPhoneCode,
  ICreateUserArguments,
  IRole,
  GetUserByIdReturnType,
  IUpdateUserArgs,
  IUserModel,
  IGetUserListArgs,
  UserContactInfo,
  IUpdateOwnUserArgs,
  InvestorProfileDetailsForUserModel,
  IUsersFilterResponse,
  IDepartmentModel,
  ICategoryModel,
} from "./types";

export async function createUserRequest(
  variables: PartialSelectedAndNullable<
    ICreateUserArguments,
    | "line"
    | "discord"
    | "twitter"
    | "telegram"
    | "slack"
    | "whatsapp"
    | "linkedin"
    | "title"
    | "environment"
  >
) {
  try {
    const request = getRequestInstance(RequestTypes.MUTATE);
    const res = await request({
      mutation: UserCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "User",
      fieldNames: [
        "usersPaginated",
        "usersFilter",
        "userListByDepartment",
        "userListForFilters",
        "userGetById",
        "userListEmployees",
      ],
    });

    store.dispatch(
      showSnackBar({
        duration: NotificationDuration.DEFAULT,
        message: successMessages.newUser,
        mode: NotificationMode.SUCCESS,
      })
    );

    return res?.data?.userCreate;
  } catch (error) {
    throw error;
  }
}

export async function getCountriesPhoneCode(): Promise<ICountryPhoneCode[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: CountryPhoneCodeListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.countryPhoneCodeList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getRolesList(): Promise<IRole[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: RoleListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.roleList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getUsersList(
  variables: IGetUserListArgs
): Promise<IGetUserReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UsersListDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.usersPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getUserById(
  userId: number
): Promise<GetUserByIdReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserGetByIdDocument,
      variables: {
        userId,
      },
      fetchPolicy: "network-only",
    });

    if (!res?.data?.userGetById) {
      return res?.data?.userGetById;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { __typename, ...data } = res.data.userGetById;

    return data;
  } catch (error) {
    throw error;
  }
}

export async function deleteUser(userId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UserDeleteDocument,
      variables: {
        userId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "User",
      id: userId.toString(),
      fieldNames: [
        "usersPaginated",
        "usersFilter",
        "userListByDepartment",
        "userGetById",
        "userListEmployees",
      ],
    });

    return Boolean(res?.data?.userDelete);
  } catch (error: any) {
    store.dispatch(
      showSnackBar({
        duration: NotificationDuration.ERRORS,
        message: error.message,
        mode: NotificationMode.ERROR,
      })
    );
    throw error;
  }
}

export async function updateUser(
  variables: IUpdateUserArgs
): Promise<Optional<Pick<IUserModel, "userId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UserUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "User",
      id: variables.userId.toString(),
      fieldNames: [
        "usersPaginated",
        "usersFilter",
        "userListByDepartment",
        "userGetById",
        "userListForFilters",
        "userListEmployees",
      ],
    });

    return res?.data?.userUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getUserContactInfo(
  userId: number
): Promise<UserContactInfo> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserContactDetailsFetchDocument,
      variables: {
        userId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.userContactDetailsFetch;
  } catch (error) {
    throw error;
  }
}

export async function updateUserOwnAccount(
  variables: IUpdateOwnUserArgs
): Promise<Optional<Pick<IUserModel, "userId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UserUpdateOwnAccountDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (variables.userId) {
      executeEviction(client, {
        typename: "User",
        id: variables.userId.toString(),
        fieldNames: ["userGetById", "usersFilter", "userListForFilters"],
      });
    }

    return res?.data?.userUpdateOwnAccount;
  } catch (error) {
    throw error;
  }
}

export async function getInvestorProfileDetailsForUser(
  investorProfileId: number
): Promise<InvestorProfileDetailsForUserModel> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserGetInvestorDetailsForCreateDocument,
      variables: { investorProfileId },
      fetchPolicy: "network-only",
    });

    return res?.data?.userGetInvestorDetailsForCreate;
  } catch (error) {
    throw error;
  }
}

export async function getUsersFilter(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<IUsersFilterResponse[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UsersFilterDocument,
      variables: {
        scope,
        resource,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.userListForFilters ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getUsersFinancialFullAccess(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<Pick<IUserModel, "userId" | "firstName" | "lastName">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserListByRoleDocument,
      variables: {
        scope,
        resource,
        roleName: "Finance Access_Full",
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.userListByRole ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getUserHOV(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<Nullable<Pick<IUserModel, "userId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserGetByManagedDepartmentDocument,
      variables: {
        scope,
        resource,
        departmentName: DepartmentEnum.VENTURE,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.userGetByManagedDepartment;
  } catch (error) {
    throw error;
  }
}

export async function getUsersByDepartment(
  resource: ResourceEnum,
  scope: ScopeEnum,
  departmentId: number
): Promise<Pick<IUserModel, "userId" | "firstName" | "lastName">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserListByDepartmentDocument,
      variables: {
        scope,
        resource,
        departmentId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.userListByDepartment ?? [];
  } catch (error) {
    throw error;
  }
}

export async function listEmployees(
  resource: ResourceEnum,
  scope: ScopeEnum,
  departmentIds?: number[]
): Promise<Pick<IUserModel, "userId" | "firstName" | "lastName">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserListEmployeesDocument,
      variables: {
        scope,
        resource,
        departmentIds,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.userListEmployees ?? [];
  } catch (error) {
    throw error;
  }
}

export async function listCrstEmployees(): Promise<
  Pick<IUserModel, "userId" | "firstName" | "lastName">[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserListCrstEmployeesDocument,
      fetchPolicy: "network-only",
    });

    return res?.data?.userListCrstEmployees ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getInvestorProfilesForUsersCreate(): Promise<
  Omit<InvestorProfilesNameReturnType, "fileNumber">[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: InvestorProfilesForUserCreationDocument,
      fetchPolicy: "no-cache",
    });

    return (res?.data?.investorProfilesForUserCreation ?? []).map(
      mapInvestorName
    );
  } catch (error) {
    throw error;
  }
}

export async function resetUserOtp(userId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UserOtpResetDocument,
      variables: {
        userId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "User",
      id: userId.toString(),
      fieldNames: ["userGetById", "usersFilter", "userListForFilters"],
    });

    return Boolean(res?.data?.userOtpReset);
  } catch (error) {
    throw error;
  }
}

export async function getDepartments(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<IDepartmentModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: DepartmentListDocument,
      variables: {
        resource,
        scope,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.departmentList ?? [];
  } catch (e) {
    throw e;
  }
}

export async function getDepartmentsForUserManagementSettings(
  userId?: number
): Promise<IDepartmentModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: DepartmentListDepartmentsCouldBeManagedDocument,
      variables: {
        userId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.departmentListDepartmentsCouldBeManaged ?? [];
  } catch (e) {
    throw e;
  }
}

export async function getUsersForUserManagementSettings(
  userId?: number
): Promise<Pick<IUsersFilterResponse, "userId" | "fullName">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserListUsersCouldBeManagedDocument,
      variables: {
        userId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.userListUsersCouldBeManaged ?? [];
  } catch (e) {
    throw e;
  }
}

export async function getCategories(): Promise<ICategoryModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UserListCategoriesDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.userListCategories ?? [];
  } catch (e) {
    throw e;
  }
}
